/**
 * The control that changes the theme.
 *
 * A button and not a link: this changes a state of the document, where the
 * language control beside it changes *which* document. The two sit together and
 * must not look like the same kind of thing.
 *
 * **It renders both icons and hides one.** Swapping which SVG is mounted would
 * make the button's width jump between themes — and, worse, would mean the
 * prerendered bytes carry the dark icon while a light-preferring reader's first
 * paint wants the other, with only a hydration pass to correct it. Both are in
 * the markup, CSS picks by `[data-theme]`, and the swap therefore happens in
 * `public/theme.js` before anything paints rather than in React afterwards.
 *
 * The label says what pressing it *does*, not what the theme is. "Koyu tema"
 * next to a moon on a dark page is a control that reads as a statement of fact,
 * and a screen-reader user has no way to tell a label from a state.
 */
import { useT } from '../i18n/context'
import { setTheme, useTheme } from '../theme'

export function ThemeToggle() {
  const t = useT()
  const theme = useTheme()
  const next = theme === 'light' ? 'dark' : 'light'

  return (
    <button
      type="button"
      className="kl-theme"
      onClick={() => setTheme(next)}
      /* The pressed state is the honest ARIA shape for a two-state control that
         stays put, and it carries the current theme to a reader who cannot see
         which icon is showing. */
      aria-pressed={theme === 'light'}
      aria-label={t(next === 'light' ? 'nav.themeToLight' : 'nav.themeToDark')}
      title={t(next === 'light' ? 'nav.themeToLight' : 'nav.themeToDark')}
    >
      {/* Sun — shown while the page is dark, because it is what pressing gives
          you. `aria-hidden`: the button already has a label, and an unlabelled
          decorative glyph announced beside it is noise. */}
      <svg className="kl-theme__sun" viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
        <circle cx="12" cy="12" r="4.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <line
            key={deg}
            x1="12"
            y1="2.6"
            x2="12"
            y2="5.2"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            transform={`rotate(${deg} 12 12)`}
          />
        ))}
      </svg>
      {/* Moon — shown while the page is light. */}
      <svg className="kl-theme__moon" viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
        <path
          d="M20.5 14.3A8.6 8.6 0 0 1 9.7 3.5a8.6 8.6 0 1 0 10.8 10.8Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  )
}
